import { css, cx } from "@emotion/css";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import { accessibilityStyles } from "../../../utils";
import type { PopoverBasicHelpProps } from "../../Popover/PopoverBasicHelp";
export interface LegendProps {
    /**
     * The label text for the legend.
     */
    label?: string;
    /**
     * PopoverBasicHelp component to display additional help information
     * displays the Information icon next to the group label
     */
    popover?: React.ReactElement<PopoverBasicHelpProps>;
    /**
     * If true displays the (optional) text next to the group label
     */
    hasOptionalMarker?: boolean;
    /**
     * If true displays the (required) text next to the group label
     */
    hasRequiredMarker?: boolean;
    /**
     * Controls whether the legend should appear disabled.
     */
    disabled?: boolean;
    /**
     * Hides the label visually but keeps it accessible to screen readers.
     */
    hideLabel?: boolean;
}
/**
 *
 * Legend renders accessible labels for form field groups with support for
 * required/optional markers, help popovers, and disabled states.
 *
 * @internal
 */
export function Legend({ label, hasOptionalMarker, hasRequiredMarker, popover, disabled, hideLabel }: LegendProps) {
    if (!label) {
        return null;
    }
    return (<legend className={cx(labelStyles, { [disabledStyles]: disabled, [accessibleHiddenStyles]: hideLabel })}>
            {label}
            {hasOptionalMarker && !hasRequiredMarker && " (optional)"}
            {hasRequiredMarker && !hasOptionalMarker && " (required)"}
            {popover}
        </legend>);
}
const labelStyles = css({
    color: themeTokens.color.text.primary,
    font: text.body.bold.large,
    padding: 0,
    display: "grid",
    gridTemplateColumns: "auto auto",
    alignItems: "center",
    gap: space[4],
    // If legend is visible but description is hidden, add margin to top of next element
    "& + p.hasHiddenDescription + *": {
        marginTop: space[16],
    },
});
const disabledStyles = css({
    color: themeTokens.color.text.tertiary,
});
const accessibleHiddenStyles = css({
    ...accessibilityStyles.hidden,
});
